import type { OrderModel, OrderTypeEnum, IOrderTableFilters, PendingForExpenseOrder } from 'src/types/order';

import axios, { endpoints } from 'src/lib/axios';

type GetOrdersParams = {
  page: number;
  pageSize?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
} & Partial<IOrderTableFilters>;

export const getOrders = async (params: GetOrdersParams) => {
  const requestToSend = {
    ...params,
    fromDate: params.fromDate ? params.fromDate.toDate() : null,
    toDate: params.toDate ? params.toDate.toDate() : null,
  };
  const response = await axios.post(endpoints.orders.getOrders, requestToSend);
  return response.data;
};

export const getOrder = async (id: number) => {
  const response = await axios.get<OrderModel>(endpoints.orders.getOrder(id));
  return response.data;
};

export const updateOrder = async (id: number, data: OrderModel) => {
  const response = await axios.put(endpoints.orders.updateOrder(id), data);
  return response.data;
};

export const addNoteToOrder = async (id: number, note: string) => {
  const response = await axios.post(endpoints.orders.addNoteToOrder(id), { note });
  return response.data;
};

export const sendOrderReminder = async (orderId: number, reminderType: number) => {
  const response = await axios.post(endpoints.orders.sendReminder, { orderId, reminderType });
  return response.data;
};

export const sendPaymentReminder = async (orderId: number) => {
  const response = await axios.post(endpoints.orders.sendPaymentReminder, { orderId });
  return response.data;
};

export const getPendingExternalPaymentsCount = async () => {
  const response = await axios.get(endpoints.orders.externals.pendingPaymentsCount);
  return response.data;
};

// Nueva función para crear órdenes externas
export const createExternalOrder = async (data: {
  orderItems: any[];
  billingDetails?: any;
  billingMethod: string;
  paymentMethod: string;
  platformId?: number;
}) => {
  const response = await axios.post(endpoints.orders.externals.createExternalOrder, data);
  return response.data;
};

export const getOrdersForCalendar = async (year: number, month: number) => {
  const response = await axios.get(endpoints.orders.calendar(year, month));
  return response.data;
};

export const downloadOrderReceipt = async (orderId: number) => {
  const response = await axios.get(endpoints.orders.downloadReceipt(orderId), {
    responseType: 'blob',
  });

  // Extraer el nombre del archivo del header Content-Disposition
  const contentDisposition = response.headers['content-disposition'];
  let filename = `recibo_pedido_${orderId}.pdf`;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Crear URL para descarga
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const searchOrdersByUrl = async (url: string): Promise<OrderModel[]> => {
  const response = await axios.get<OrderModel[]>(endpoints.orders.searchByUrl, {
    params: { url },
  });
  return response.data;
};

export const getPendingOrdersForExpense = async (
  orderType: OrderTypeEnum
): Promise<PendingForExpenseOrder[]> => {
  const response = await axios.get<PendingForExpenseOrder[]>(
    endpoints.orders.pendingForExpense,
    { params: { orderType } }
  );
  return response.data;
};

export const previewRecalculateCostPrice = async (orderId: number) => {
  const response = await axios.get(endpoints.orders.recalculateCostPreview(orderId));
  return response.data;
};

export const applyRecalculateCostPrice = async (orderId: number) => {
  const response = await axios.post(endpoints.orders.recalculateCostApply(orderId));
  return response.data;
};
